import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import { IEventRequestParams, IPaginationParams, ISortRequestParams, replaceSortRequestParamsWithSortString, } from '../shared.dto';
import {
    ICreateOrganizationDTO,
    IDeleteOrganizationDTO,
    IIgnoreAttendeesRequest,
    IOrganizationDetailsDTO,
    IOrganizationsDAO,
    IOrganizationsListDTO,
    IOrganizationTypesDTO,
    IUpdateOrganizationDTO,
    TGetOrganizationsSortKeys,
} from '../organizations.dao';

import {
    CREATE_ORGANIZATION_URL,
    EXPORT_LINKS_ORGANIZATIONS_URL,
    EXPORT_ORGANIZATIONS_URL,
    GET_CHAPERONES_URL,
    GET_ORGANIZATION_URL,
    GET_ORGANIZATIONS_TYPES_URL,
    IMPORT_ORGANIZATIONS_URL,
    ORGANIZATION_LIST_URL,
    UPDATE_ORGANIZATION_URL
} from '../../constants/api/organization-api.constants';
import { JobDtoType } from '../../domain/jobs';
import { IUserDTO } from '../user-groups.dao';

class OrganizationsDAO extends IOrganizationsDAO {

    constructor(@IoC.Inject private readonly apiClient: IApiClient) {
        super();
    }

    public async getOrganizations(
        params: IIgnoreAttendeesRequest & IEventRequestParams
            & ISortRequestParams<TGetOrganizationsSortKeys>
            & IPaginationParams
    ): Promise<IOrganizationsListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        /* istanbul ignore next */
        const requestParams = params?.size
            ? { ...paramsWithSortString } // to return records with splitting by pages
            : { ...paramsWithSortString, size: 10_000_000 }; // to return all records without splitting by pages
        const response = await this.apiClient.get<IOrganizationsListDTO>(ORGANIZATION_LIST_URL, {
            ...requestParams,
        });
        return response.parsedResponse;
    }

    public async getAllOrganizations(params: IIgnoreAttendeesRequest & IEventRequestParams): Promise<IOrganizationsListDTO> {
        /* max size of organization API able to return */
        const maxSize = 2000;

        const callApi = async (page: number = 0, organizations: IOrganizationDetailsDTO[] = []) => {
            const reqParams = { ...params, page: page, size: maxSize };
            const { parsedResponse } = await this.apiClient.get<IOrganizationsListDTO>(ORGANIZATION_LIST_URL, reqParams);
            organizations = organizations.concat(parsedResponse.content);

            if (page + 1 >= parsedResponse.totalPages) {
                return {
                    content: organizations,
                    totalElements: parsedResponse.totalElements,
                    totalPages: parsedResponse.totalPages
                };
            }
            return await callApi(page + 1, organizations);
        };

        return await callApi();
    }

    public async createOrganization(params: ICreateOrganizationDTO & IEventRequestParams): Promise<void> {
        await this.apiClient.post<ICreateOrganizationDTO & IEventRequestParams>(CREATE_ORGANIZATION_URL, params);
    }

    public async updateOrganization(params: IUpdateOrganizationDTO & IEventRequestParams): Promise<void> {
        await this.apiClient.put<IUpdateOrganizationDTO & IEventRequestParams>(UPDATE_ORGANIZATION_URL, params);
    }

    public async getOrganizationDetails(params: IEventRequestParams & { organizationId: number }): Promise<IOrganizationDetailsDTO> {
        const response = await this.apiClient.get<IOrganizationDetailsDTO>(GET_ORGANIZATION_URL, params);
        return response.parsedResponse;
    }

    public async importOrganizations(params: IEventRequestParams & {
        file: File;
    }) {
         const response = await this.apiClient.post<JobDtoType>(IMPORT_ORGANIZATIONS_URL, {
            accountId: params.accountId,
            eventId: params.eventId,
            file: params.file,
            separator: ','
        }, {
            ContentType: RequestContentType.formData,
        });
        return response.parsedResponse;
    }

    public async getOrganizationsCSV(params: IEventRequestParams): Promise<void> {
        await this.apiClient.getFile(EXPORT_ORGANIZATIONS_URL, params);
    }

    public async deleteOrganization(params: IDeleteOrganizationDTO): Promise<JobDtoType> {
        const response = await this.apiClient.delete<JobDtoType>(UPDATE_ORGANIZATION_URL, params);
        return response.parsedResponse;
    }

    public async getOrganizationsLinksCSV(params: IEventRequestParams): Promise<void> {
        await this.apiClient.getFile(EXPORT_LINKS_ORGANIZATIONS_URL, params);
    }


    public async getChaperones(params: IEventRequestParams): Promise<IUserDTO[]> {
        const response = await this.apiClient.get<IUserDTO[]>(GET_CHAPERONES_URL, params);
        return response.parsedResponse;
    }

    public async getOrganizationTypes(params: IEventRequestParams & IPaginationParams): Promise<IOrganizationTypesDTO> {
        const response = await this.apiClient.get<IOrganizationTypesDTO>(GET_ORGANIZATIONS_TYPES_URL, params);
        return response.parsedResponse;
    }
}

export { OrganizationsDAO };
